import { useCallback } from 'react';
import { useRecoilValue } from 'recoil';
import { CrossSectionTool } from '~/engine/tool/tools/contouring-tools/cross-section/CrossSectionTool';
import { viewManagerState } from '~/state/atoms/engineAtoms';
import { crossSectionDrawingHistoryState } from '~/state/atoms/toolsAtoms';

/**
 * Undo, Redo and Clear for cross-sections drawn but not applied; undo and
 * redo return false when no step was taken, including with no tool armed.
 */
export function useCrossSectionDrawingHistory() {
    const { canUndo, canRedo, hasDrawing } = useRecoilValue(
        crossSectionDrawingHistoryState
    );
    const toolManager =
        useRecoilValue(viewManagerState)?.interactionContext.toolManager;
    const withTool = useCallback(
        <T>(
            what: string,
            act: (tool: CrossSectionTool) => T,
            refused: T
        ): T => {
            const tool = toolManager?.getActiveTool();
            if (!(tool instanceof CrossSectionTool)) {
                // Reported, not absorbed: the drawn state is out of sync with the tools.
                console.error(
                    `Cross-section drawing ${what} requested with no ` +
                        'cross-section tool armed — the drawn state is stale.'
                );
                return refused;
            }
            return act(tool);
        },
        [toolManager]
    );
    const undo = useCallback(
        () => withTool('undo', (tool) => tool.undoDrawing(), false),
        [withTool]
    );
    const redo = useCallback(
        () => withTool('redo', (tool) => tool.redoDrawing(), false),
        [withTool]
    );
    const clear = useCallback(
        () =>
            withTool(
                'clear',
                (tool) => tool.clearAllCrossSections(),
                undefined
            ),
        [withTool]
    );
    return { canUndo, undo, canRedo, redo, hasDrawing, clear };
}
